/**
 * components/theme.ts — l'état du thème de l'atelier, en un seul endroit.
 *
 * localStorage est un système externe : on s'y abonne plutôt que d'en recopier
 * la valeur dans un état React au montage. Ce qui se peint en CSS n'a besoin de
 * rien de tout ça — seul ce qui se peint en JS (un canvas, une carte MapLibre)
 * doit savoir quel thème est actif, faute de pouvoir lire une variable CSS.
 */

export type LabTheme = 'light' | 'dark';

export const CLE_THEME = 'ogryn.theme';

/** Le jour est la base ; la nuit devient l'option. */
export const THEME_DEFAUT: LabTheme = 'light';

let ecouteurs: (() => void)[] = [];

// A-t-on déjà écrit le thème DEPUIS cette page (clic sur la bascule, ou
// synchronisation venue de la base) ? Sert de garde à SyncPreferences.tsx :
// sans elle, un compte dont la base garde un thème périmé (`dark` enregistré
// il y a des semaines, avant que la personne ne repasse en clair sur cet
// appareil) écrase le choix qu'elle vient de faire dès que la requête
// `users.me` répond — la bascule locale gagne l'instant du clic, la base
// reprend la main une seconde plus tard, sans que rien à l'écran n'explique
// pourquoi. Trouvé le 01/09 : deux comptes de test montraient déjà des valeurs
// `theme` divergentes entre elles en base, signe que cette course arrive en
// vrai, pas seulement en théorie.
let toucheParUtilisateur = false;
export const themeToucheParUtilisateur = () => toucheParUtilisateur;
/** Appelé par la bascule elle-même (clic), jamais par la synchronisation base. */
export const signalerChoixUtilisateur = () => { toucheParUtilisateur = true; };

export const themeStore = {
  subscribe(cb: () => void) {
    ecouteurs.push(cb);
    return () => { ecouteurs = ecouteurs.filter(e => e !== cb); };
  },
  /**
   * L'ATTRIBUT du DOM fait foi, localStorage n'est que son repli.
   *
   * Le script du layout pose `data-theme` avant l'hydratation ; c'est donc lui
   * qui décrit ce qui est réellement peint. Lire localStorage en premier
   * laissait les deux diverger : l'icône annonçait la nuit pendant que l'écran
   * était en jour, et il fallait deux clics pour que la bascule reprenne la
   * main — le premier ne faisait que recaler l'état sur ce qu'on voyait déjà.
   */
  lire(): LabTheme {
    const dom = document.documentElement.getAttribute('data-theme');
    if (dom === 'dark' || dom === 'light') return dom;
    const v = localStorage.getItem(CLE_THEME);
    return v === 'dark' || v === 'light' ? v : THEME_DEFAUT;
  },
  serveur: (): LabTheme => THEME_DEFAUT,
  ecrire(t: LabTheme) {
    // Le DOM est mis à jour ICI, pas dans un effet : le clic doit peindre au
    // moment où il est reçu, sans dépendre du cycle de rendu de React.
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem(CLE_THEME, t);
    ecouteurs.forEach(e => e());
  },
};

/**
 * Résout une variable CSS en valeur réelle. Nécessaire pour tout ce qui est
 * peint hors du DOM — un canvas ne sait pas ce qu'est `var(--otto-pixel)`.
 */
export function valeurCss(expr: string, repli: string): string {
  if (!expr.startsWith('var(')) return expr;
  if (typeof document === 'undefined') return repli;
  const nom = expr.slice(4, -1).split(',')[0].trim();
  return getComputedStyle(document.documentElement).getPropertyValue(nom).trim() || repli;
}
